/**
 * Calendar Connections feature — Redux slice (ducks pattern).
 *
 * Tracks the set of Apple/Google calendar import connections owned by
 * the current user, plus transient state for the connect modal:
 * setup result (Apple-discovered calendars after credentials check),
 * Google calendar list (after OAuth callback), and the connection id
 * currently being managed in the post-connect picker.
 */

import type { CalendarConnection, GoogleCalendar, AppleCalendarDiscoveredCalendar, Subcalendar } from '@/infra/contracts/calendar';

// ─── Types ────────────────────────────────────────────────

export type CalendarConnectionsModalStep =
  | 'closed'
  | 'choose-provider'
  | 'apple-credentials'
  | 'subcalendar-picker'
  | 'manage-subcalendars';

export interface CalendarConnectionsState {
  connections: CalendarConnection[];
  loading: boolean;
  error: string | null;
  // Connect-modal state
  modalStep: CalendarConnectionsModalStep;
  modalProvider: 'apple' | 'google' | null;
  // Discovered calendars from AppleCalendarSetup or ListGoogleCalendars
  discoveredApple: AppleCalendarDiscoveredCalendar[];
  discoveredGoogle: GoogleCalendar[];
  // Current Apple credentials, kept in memory only to bridge setup → connect
  pendingApple: { email: string; appSpecificPassword: string } | null;
  // The connection currently being edited (post-connect picker, or
  // re-opened via "Manage" on an existing row).
  managingConnectionId: string | null;
  modalSubmitting: boolean;
  modalError: string | null;
  // Subcalendars for the connection currently being managed.
  manageSubcalendars: Subcalendar[];
}

export const initialState: CalendarConnectionsState = {
  connections: [],
  loading: false,
  error: null,
  modalStep: 'closed',
  modalProvider: null,
  discoveredApple: [],
  discoveredGoogle: [],
  pendingApple: null,
  managingConnectionId: null,
  modalSubmitting: false,
  modalError: null,
  manageSubcalendars: [],
};

// ─── Action Types ─────────────────────────────────────────

const SET_CONNECTIONS = 'calendarConnections/setConnections';
const SET_LOADING = 'calendarConnections/setLoading';
const SET_ERROR = 'calendarConnections/setError';
const OPEN_MODAL = 'calendarConnections/openModal';
const CLOSE_MODAL = 'calendarConnections/closeModal';
const SET_MODAL_STEP = 'calendarConnections/setModalStep';
const SET_MODAL_PROVIDER = 'calendarConnections/setModalProvider';
const SET_DISCOVERED_APPLE = 'calendarConnections/setDiscoveredApple';
const SET_DISCOVERED_GOOGLE = 'calendarConnections/setDiscoveredGoogle';
const SET_PENDING_APPLE = 'calendarConnections/setPendingApple';
const SET_MANAGING = 'calendarConnections/setManaging';
const SET_MODAL_SUBMITTING = 'calendarConnections/setModalSubmitting';
const SET_MODAL_ERROR = 'calendarConnections/setModalError';
const SET_MANAGE_SUBCALENDARS = 'calendarConnections/setManageSubcalendars';
const PATCH_MANAGE_SUBCALENDAR = 'calendarConnections/patchManageSubcalendar';
export const CONNECTION_SYNC_COMPLETED = 'calendarConnections/connectionSyncCompleted';

// Trigger actions (consumed by sagas)
export const FETCH_CONNECTIONS = 'calendarConnections/fetchConnections';
export const APPLE_SETUP = 'calendarConnections/appleSetup';
export const APPLE_CONNECT = 'calendarConnections/appleConnect';
export const GOOGLE_BEGIN = 'calendarConnections/googleBegin';
export const GOOGLE_AFTER_CALLBACK = 'calendarConnections/googleAfterCallback';
export const SAVE_SELECTION = 'calendarConnections/saveSelection';
export const SYNC_CONNECTION = 'calendarConnections/syncConnection';
export const DISCONNECT_CONNECTION = 'calendarConnections/disconnect';
export const OPEN_MANAGE = 'calendarConnections/openManage';
export const UPDATE_SUBCAL = 'calendarConnections/updateSubcalendar';

// ─── Action Creators ──────────────────────────────────────

export function setConnections(connections: CalendarConnection[]) {
  return { type: SET_CONNECTIONS, payload: connections } as const;
}

export function setConnectionsLoading(loading: boolean) {
  return { type: SET_LOADING, payload: loading } as const;
}

export function setConnectionsError(error: string | null) {
  return { type: SET_ERROR, payload: error } as const;
}

export function openConnectModal() {
  return { type: OPEN_MODAL } as const;
}

export function closeConnectModal() {
  return { type: CLOSE_MODAL } as const;
}

export function setModalStep(step: CalendarConnectionsModalStep) {
  return { type: SET_MODAL_STEP, payload: step } as const;
}

export function setModalProvider(provider: 'apple' | 'google' | null) {
  return { type: SET_MODAL_PROVIDER, payload: provider } as const;
}

export function setDiscoveredApple(calendars: AppleCalendarDiscoveredCalendar[]) {
  return { type: SET_DISCOVERED_APPLE, payload: calendars } as const;
}

export function setDiscoveredGoogle(calendars: GoogleCalendar[]) {
  return { type: SET_DISCOVERED_GOOGLE, payload: calendars } as const;
}

export function setPendingApple(creds: { email: string; appSpecificPassword: string } | null) {
  return { type: SET_PENDING_APPLE, payload: creds } as const;
}

export function setManaging(connectionId: string | null) {
  return { type: SET_MANAGING, payload: connectionId } as const;
}

export function setModalSubmitting(submitting: boolean) {
  return { type: SET_MODAL_SUBMITTING, payload: submitting } as const;
}

export function setModalError(error: string | null) {
  return { type: SET_MODAL_ERROR, payload: error } as const;
}

export function setManageSubcalendars(rows: Subcalendar[]) {
  return { type: SET_MANAGE_SUBCALENDARS, payload: rows } as const;
}

export function patchManageSubcalendar(id: string, patch: Partial<Subcalendar>) {
  return { type: PATCH_MANAGE_SUBCALENDAR, payload: { id, patch } } as const;
}

/**
 * Emitted by applySyncOutcomeSaga after a sync RPC succeeds. Patches the
 * connection row in place — clears stale-indicator state and (when the
 * response carried them) bumps last_synced_at / status — instead of a
 * heavy list refetch. Per feedback_saga_full_delta the sync response IS
 * the delta for the connection's own row; per-event deltas land in a
 * follow-up task (CalendarConnectionDelta proto foundation, task 21).
 */
export function connectionSyncCompleted(
  id: string,
  patch?: { last_synced_at?: string; status?: string },
) {
  return { type: CONNECTION_SYNC_COMPLETED, payload: { id, patch: patch ?? {} } } as const;
}

// Trigger: dispatch a row-level update; the saga calls UpdateSubcalendar
// and applies the optimistic patch immediately.
export function updateSubcalendar(
  id: string,
  patch: { enabled?: boolean; color?: string; assignedOwnerId?: string },
) {
  return { type: UPDATE_SUBCAL, payload: { id, patch } } as const;
}

// Trigger creators
export function fetchCalendarConnections() {
  return { type: FETCH_CONNECTIONS } as const;
}

export function appleSetup(email: string, appSpecificPassword: string) {
  return { type: APPLE_SETUP, payload: { email, appSpecificPassword } } as const;
}

export function appleConnect(args: {
  email: string;
  appSpecificPassword: string;
  selectedCalendarUris: string[];
}) {
  return { type: APPLE_CONNECT, payload: args } as const;
}

export function googleBegin() {
  return { type: GOOGLE_BEGIN } as const;
}

export function googleAfterCallback() {
  return { type: GOOGLE_AFTER_CALLBACK } as const;
}

export function saveSelection(connectionId: string, selectedIds: string[]) {
  return { type: SAVE_SELECTION, payload: { connectionId, selectedIds } } as const;
}

export function syncConnection(connectionId: string, provider: string) {
  return { type: SYNC_CONNECTION, payload: { connectionId, provider } } as const;
}

export function disconnectConnection(connectionId: string, provider: string) {
  return { type: DISCONNECT_CONNECTION, payload: { connectionId, provider } } as const;
}

export function openManage(connectionId: string) {
  return { type: OPEN_MANAGE, payload: connectionId } as const;
}

// ─── Reducer ──────────────────────────────────────────────

type Action = { type: string; payload?: any };

export function calendarConnectionsReducer(
  state: CalendarConnectionsState = initialState,
  action: Action
): CalendarConnectionsState {
  switch (action.type) {
    case SET_CONNECTIONS:
      return { ...state, connections: action.payload };
    case SET_LOADING:
      return { ...state, loading: action.payload };
    case SET_ERROR:
      return { ...state, error: action.payload };
    case OPEN_MODAL:
      return {
        ...state,
        modalStep: 'choose-provider',
        modalProvider: null,
        discoveredApple: [],
        discoveredGoogle: [],
        pendingApple: null,
        managingConnectionId: null,
        modalError: null,
        manageSubcalendars: [],
      };
    case CLOSE_MODAL:
      return {
        ...state,
        modalStep: 'closed',
        modalProvider: null,
        discoveredApple: [],
        discoveredGoogle: [],
        pendingApple: null,
        managingConnectionId: null,
        modalSubmitting: false,
        modalError: null,
        manageSubcalendars: [],
      };
    case SET_MODAL_STEP:
      return { ...state, modalStep: action.payload };
    case SET_MODAL_PROVIDER:
      return { ...state, modalProvider: action.payload };
    case SET_DISCOVERED_APPLE:
      return { ...state, discoveredApple: action.payload };
    case SET_DISCOVERED_GOOGLE:
      return { ...state, discoveredGoogle: action.payload };
    case SET_PENDING_APPLE:
      return { ...state, pendingApple: action.payload };
    case SET_MANAGING:
      return { ...state, managingConnectionId: action.payload };
    case SET_MODAL_SUBMITTING:
      return { ...state, modalSubmitting: action.payload };
    case SET_MODAL_ERROR:
      return { ...state, modalError: action.payload };
    case SET_MANAGE_SUBCALENDARS:
      return { ...state, manageSubcalendars: action.payload };
    case PATCH_MANAGE_SUBCALENDAR: {
      const { id, patch } = action.payload as { id: string; patch: Partial<Subcalendar> };
      return {
        ...state,
        manageSubcalendars: state.manageSubcalendars.map((s) => s.id === id ? { ...s, ...patch } : s),
      };
    }
    case CONNECTION_SYNC_COMPLETED: {
      const { id, patch } = action.payload as {
        id: string;
        patch: { last_synced_at?: string; status?: string };
      };
      return {
        ...state,
        connections: state.connections.map((c) =>
          c.id === id ? { ...c, ...patch } : c,
        ),
      };
    }
    default:
      return state;
  }
}
